"use client";
import Link from "next/link";
import { useState } from "react";
import { useModal } from "@/hooks/useModal";
import { FormModal } from "@/components/common";
import CustomSelect from "@/components/ui/custom-select";
import type { FormData as ModalFormData } from "@/types/modal";
import {
    getCountryOptions,
    validatePhoneNumber,
    formatPhoneNumber,
    formatPhoneWithSpaces,
    getPlaceholder,
    getMaxLength
} from "@/lib/phone-validation";
import "@/components/ui/custom-select.css";

interface ContactFormData {
    name: string;
    email: string;
    countryCode: string;
    phone: string;
    budget: string;
    projectType: string[];
    startTimeline: string;
    hearAboutUs: string;
    message: string;
    file: File | null;
    agreeToContact: boolean;
    ipAddress?: string;
    location?: {
        country?: string;
        region?: string;
        city?: string;
        timezone?: string;
    };
    userAgent?: string;
}

interface FormErrors {
    name?: string;
    email?: string;
    phone?: string;
    budget?: string;
    projectType?: string;
    startTimeline?: string;
    message?: string;
    agreeToContact?: string;
}

const ContactForm = () => {
    const modal = useModal();
    const [formData, setFormData] = useState<ContactFormData>({
        name: "",
        email: "",
        countryCode: "IN",
        phone: "",
        budget: "",
        projectType: [],
        startTimeline: "",
        hearAboutUs: "",
        message: "",
        file: null,
        agreeToContact: false,
        ipAddress: undefined,
        location: undefined,
        userAgent: undefined,
    });

    // Select options data
    const budgetOptions = [
        { value: "Under $10,000", label: "Under $10,000" },
        { value: "$10,000 - $25,000", label: "$10,000 - $25,000" },
        { value: "$25,000 - $50,000", label: "$25,000 - $50,000" },
        { value: "$50,000 - $100,000", label: "$50,000 - $100,000" },
        { value: "$100,000 - $250,000", label: "$100,000 - $250,000" },
        { value: "$250,000 - $500,000", label: "$250,000 - $500,000" },
        { value: "Over $500,000", label: "Over $500,000" },
        { value: "Not sure yet", label: "Not sure yet" }
    ];

    const projectTypeOptions = [
        { value: "Web Application", label: "Web Application" },
        { value: "Mobile App", label: "Mobile App" },
        { value: "E-commerce Platform", label: "E-commerce Platform" },
        { value: "SaaS Product", label: "SaaS Product" },
        { value: "API Development", label: "API Development" },
        { value: "Data Analytics Platform", label: "Data Analytics Platform" },
        { value: "Other", label: "Other" }
    ];

    const startTimelineOptions = [
        { value: "Immediately", label: "Immediately" },
        { value: "Within 1 month", label: "Within 1 month" },
        { value: "Within 3 months", label: "Within 3 months" },
        { value: "Within 6 months", label: "Within 6 months" },
        { value: "Just exploring", label: "Just exploring" }
    ];

    const hearAboutUsOptions = [
        { value: "Google Search", label: "Google Search" },
        { value: "Social Media", label: "Social Media" },
        { value: "Referral", label: "Referral" },
        { value: "Previous Client", label: "Previous Client" },
        { value: "Industry Event", label: "Industry Event" },
        { value: "Other", label: "Other" }
    ];

    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{
        type: 'success' | 'error' | null;
        message: string;
    }>({ type: null, message: '' });

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
        } else if (formData.name.trim().length < 2) {
            newErrors.name = "Name must be at least 2 characters";
        }

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Please enter a valid email address";
        }

        // Phone validation with country-specific rules
        const phoneValidation = validatePhoneNumber(formData.phone, formData.countryCode);
        if (!phoneValidation.isValid) {
            newErrors.phone = phoneValidation.error || "Invalid phone number";
        }

        // Project type validation
        if (formData.projectType.length === 0) {
            newErrors.projectType = "Project type is required";
        }

        // Start timeline validation
        if (!formData.startTimeline.trim()) {
            newErrors.startTimeline = "Start timeline is required";
        }

        // Message validation
        if (!formData.message.trim()) {
            newErrors.message = "Message is required";
        } else if (formData.message.trim().length < 10) {
            newErrors.message = "Message must be at least 10 characters";
        } else if (formData.message.trim().length > 1000) {
            newErrors.message = "Message cannot be more than 1000 characters";
        }


        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            // Enforce client caps to avoid server-side validation hits
            let nextValue = value;
            if (name === 'phone') {
                // Format phone number with spaces based on country pattern
                const digitsOnly = formatPhoneNumber(value).slice(0, getMaxLength(formData.countryCode));
                nextValue = formatPhoneWithSpaces(digitsOnly, formData.countryCode);
            } else if (name === 'message') {
                nextValue = value.slice(0, 1000);
            } else if (name === 'name') {
                nextValue = value.slice(0, 100);
            } else if (name === 'budget') {
                nextValue = value.slice(0, 50);
            } else if (name === 'projectType') {
                // For multi-select, value handling is different, primarily checked in validation
                // Typescript might complain if we try to slice an array as string.
                // Since this block is for input change mainly, and custom select uses handleSelectChange
                // we might not need this. But if we did:
                // nextValue = value; 
            } else if (name === 'hearAboutUs') {
                nextValue = value.slice(0, 200);
            }

            setFormData(prev => ({
                ...prev,
                [name]: nextValue
            }));
        }

        // Clear error when user starts typing
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handleSelectChange = (name: string, value: string | string[]) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user makes a selection
        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setFormData(prev => ({
            ...prev,
            file: file
        }));
    };

    const handleFileRemove = () => {
        setFormData(prev => ({
            ...prev,
            file: null
        }));
        // Reset the file input
        const fileInput = document.getElementById('file-upload') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Function to get user's IP and location
    const getUserLocationData = async () => {
        try {
            // Get IP address
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            const ipData = await ipResponse.json();

            // Get location data from IP
            const locationResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
            const locationData = await locationResponse.json();

            return {
                ipAddress: ipData.ip,
                location: {
                    country: locationData.country_name,
                    region: locationData.region,
                    city: locationData.city,
                    timezone: locationData.timezone,
                },
                userAgent: navigator.userAgent,
            };
        } catch (error) {
            console.warn('Could not fetch location data:', error);
            return {
                ipAddress: undefined,
                location: undefined,
                userAgent: navigator.userAgent,
            };
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus({ type: null, message: '' });

        try {
            // Get user location data
            const locationData = await getUserLocationData();

            let response: Response;

            if (formData.file) {
                // Use multipart/form-data when a file is present
                const multipart = new FormData();
                multipart.append('name', formData.name.trim());
                multipart.append('email', formData.email.trim());
                multipart.append('countryCode', formData.countryCode);
                multipart.append('phone', formData.phone.trim());
                multipart.append('budget', formData.budget.trim());
                multipart.append('projectType', formData.projectType.join(', '));
                multipart.append('hearAboutUs', formData.hearAboutUs.trim());
                multipart.append('message', formData.message.trim());
                multipart.append('file', formData.file);

                // Add location data to form data
                if (locationData.ipAddress) {
                    multipart.append('ipAddress', locationData.ipAddress);
                }
                if (locationData.userAgent) {
                    multipart.append('userAgent', locationData.userAgent);
                }
                if (locationData.location) {
                    multipart.append('location', JSON.stringify(locationData.location));
                }

                response = await fetch('/api/contact', {
                    method: 'POST',
                    body: multipart,
                });
            } else {
                // Fallback to JSON when no file is present
                const payload = {
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    countryCode: formData.countryCode,
                    phone: formData.phone.trim(),
                    budget: formData.budget.trim(),
                    projectType: formData.projectType.join(', '),
                    hearAboutUs: formData.hearAboutUs.trim(),
                    message: formData.message.trim(),
                    ipAddress: locationData.ipAddress,
                    location: locationData.location,
                    userAgent: locationData.userAgent,
                };

                response = await fetch('/api/contact', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });
            }

            const data = await response.json();

            if (response.ok) {
                setSubmitStatus({
                    type: 'success',
                    message: 'Thank you for your message! We\'ll get back to you soon.'
                });
                // Reset form
                setFormData({
                    name: "",
                    email: "",
                    countryCode: "IN",
                    phone: "",
                    budget: "",
                    projectType: [],
                    startTimeline: "",
                    hearAboutUs: "",
                    message: "",
                    file: null,
                    agreeToContact: false
                });
            } else {
                // Prefer structured field errors from API
                if (response.status === 400 && data && data.errors && typeof data.errors === 'object') {
                    const fieldErrors: FormErrors = {};
                    if (typeof data.errors.name === 'string') fieldErrors.name = data.errors.name;
                    if (typeof data.errors.email === 'string') fieldErrors.email = data.errors.email;
                    if (typeof data.errors.phone === 'string') fieldErrors.phone = data.errors.phone;
                    if (typeof data.errors.projectType === 'string') fieldErrors.projectType = data.errors.projectType;
                    if (typeof data.errors.startTimeline === 'string') fieldErrors.startTimeline = data.errors.startTimeline;
                    if (typeof data.errors.message === 'string') fieldErrors.message = data.errors.message;
                    if (typeof data.errors.agreeToContact === 'string') fieldErrors.agreeToContact = data.errors.agreeToContact;
                    setErrors(fieldErrors);
                    setSubmitStatus({ type: null, message: '' });
                } else {
                    setSubmitStatus({
                        type: 'error',
                        message: (data && data.error) || 'Something went wrong. Please try again.'
                    });
                }
            }
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitStatus({
                type: 'error',
                message: 'Network error. Please check your connection and try again.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormSubmit = (data: ModalFormData) => {
        console.log("Modal form submitted:", data);
        // Handle form submission logic here
        // You can integrate with your backend API or form service
    };

    return (
        <>
            <section
                id="desilo-contact-section"
                className="desilo-contact-section bg-color-white section-padding-top-bottom"
            >
                <div className="container custom-container">
                    <div className="row g-4 justify-content-between">
                        <div className="col-lg-6 col-xxl-5">
                            <div className="desilo-contact__content">
                                <h2 className="title">Work with us</h2>
                                <p>
                                    We read every note and route it to the right person. You will hear back within one business day.
                                </p>
                                <div className="addresses-wrapper">
                                    <div className="address-item">
                                        <div className="address-header">
                                            <img src="/images/IND.png" alt="India Flag" className="office-flag" />
                                            <h4 className="office-title">India</h4>
                                        </div>
                                        <address className="office-address">
                                            808, Sun Central Place, Ambali,
                                            Ahmedabad, India - 380058
                                        </address>
                                        <div className="office-contact">
                                            <Link href="tel:+919033939547" className="phone-link">
                                                +91 90339 39547
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="address-item">
                                        <div className="address-header">
                                            <img src="/images/USA.png" alt="USA Flag" className="office-flag" />
                                            <h4 className="office-title">USA</h4>
                                        </div>
                                        <address className="office-address">
                                            1732 Aviation Blvd #209, Redondo Beach, CA 90278, USA
                                        </address>
                                        <div className="office-contact">
                                            <Link href="tel:+14152036413" className="phone-link">
                                                +1 415 203 6413
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                                <div className="contact-info-wrapper mt-4">
                                    <div className="contact-link-item">
                                        <Link href="mailto:hello@desilo.studio" className="email-link">
                                            hello@desilo.studio
                                        </Link>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div className="col-lg-6 col-xxl-6">
                            <div className="form-section modern-form-card">
                                <div className="mb-4">
                                    <h3>
                                        Tell us about your product
                                    </h3>
                                    <p className="pt-1">
                                        A few quick details help us prep something useful before we talk.
                                    </p>
                                </div>
                                {/* Success/Error Messages */}
                                {submitStatus.type && (
                                    <div className={`alert ${submitStatus.type === 'success' ? 'alert-success' : 'alert-danger'} mt-4`}>
                                        {submitStatus.message}
                                    </div>
                                )}
                                <form
                                    onSubmit={handleSubmit}
                                    className="desilo-contact__form"
                                    autoComplete="off"
                                >
                                    <div className="row g-3 g-xl-4">
                                        {/* Row 1: Name and Email */}
                                        <div className="col-md-6">
                                            <input
                                                type="text"
                                                name="name"
                                                placeholder="Name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className={`form-control modern-input ${errors.name ? 'is-invalid' : ''}`}
                                                disabled={isSubmitting}
                                            />
                                            {errors.name && (
                                                <div className="custom-select-error">
                                                    {errors.name}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6">
                                            <input
                                                type="email"
                                                name="email"
                                                placeholder="Email address"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className={`form-control modern-input ${errors.email ? 'is-invalid' : ''}`}
                                                disabled={isSubmitting}
                                            />
                                            {errors.email && (
                                                <div className="custom-select-error">
                                                    {errors.email}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 2: Phone with Country Code and Budget */}
                                        <div className="col-md-6">
                                            <div className="phone-input-container">
                                                <div className="country-code-prefix-select">
                                                    <CustomSelect
                                                        value={formData.countryCode}
                                                        onChange={(value) => handleSelectChange('countryCode', value as string)}
                                                        options={getCountryOptions()}
                                                        placeholder="Country"
                                                        disabled={isSubmitting}
                                                        showFlags={true}
                                                        compact={true}
                                                    />
                                                </div>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    placeholder={getPlaceholder(formData.countryCode)}
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    className={`form-control modern-input phone-with-prefix ${errors.phone ? 'is-invalid' : ''}`}
                                                    disabled={isSubmitting}
                                                    maxLength={getPlaceholder(formData.countryCode).length}
                                                />
                                            </div>
                                            {errors.phone && (
                                                <div className="custom-select-error">
                                                    {errors.phone}
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-md-6">
                                            <CustomSelect
                                                value={formData.budget}
                                                onChange={(value) => handleSelectChange('budget', value)}
                                                options={budgetOptions}
                                                placeholder="Budget (USD)"
                                                disabled={isSubmitting}
                                                error={errors.budget}
                                            />
                                        </div>

                                        {/* Row 3: What are you building and When do you want to start */}
                                        <div className="col-md-6">
                                            <CustomSelect
                                                value={formData.projectType}
                                                onChange={(value) => handleSelectChange('projectType', value)}
                                                options={projectTypeOptions}
                                                placeholder="What are you building"
                                                disabled={isSubmitting}
                                                error={errors.projectType}
                                                isMulti={true}
                                                maxItems={4}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <CustomSelect
                                                value={formData.startTimeline}
                                                onChange={(value) => handleSelectChange('startTimeline', value)}
                                                options={startTimelineOptions}
                                                placeholder="When do you want to start*"
                                                disabled={isSubmitting}
                                                error={errors.startTimeline}
                                            />
                                        </div>

                                        {/* Row 4: How did you hear about us */}
                                        <div className="col-12">
                                            <CustomSelect
                                                value={formData.hearAboutUs}
                                                onChange={(value) => handleSelectChange('hearAboutUs', value)}
                                                options={hearAboutUsOptions}
                                                placeholder="How did you hear about us?"
                                                disabled={isSubmitting}
                                            />
                                        </div>

                                        {/* File Upload */}
                                        <div className="col-12">
                                            {formData.file ? (
                                                <div className="file-selected-pill">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="file-icon">
                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M14 2.26946V6.4C14 6.96005 14 7.24008 14.109 7.45399C14.2049 7.64215 14.3578 7.79513 14.546 7.89101C14.7599 8 15.0399 8 15.6 8H19.7305M20 9.98822V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V6.8C4 5.11984 4 4.27976 4.32698 3.63803C4.6146 3.07354 5.07354 2.6146 5.63803 2.32698C6.27976 2 7.11984 2 8.8 2H12.0118C12.7455 2 13.1124 2 13.4577 2.08289C13.7638 2.15638 14.0564 2.27759 14.3249 2.44208C14.6276 2.6276 14.887 2.88703 15.4059 3.40589L18.5941 6.59411C19.113 7.11297 19.3724 7.3724 19.5579 7.67515C19.7224 7.94356 19.8436 8.2362 19.9171 8.5423C20 8.88757 20 9.25445 20 9.98822Z" stroke="#0F0F0F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                            </svg>
                                                        </div>
                                                        <span className="file-name">{formData.file.name}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleFileRemove}
                                                        className="file-remove-btn"
                                                        disabled={isSubmitting}
                                                    >
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M9 3H15M3 6H21M19 6L18.2987 16.5193C18.1935 18.0975 18.1409 18.8867 17.8 19.485C17.4999 20.0118 17.0472 20.4353 16.5017 20.6997C15.882 21 15.0911 21 13.5093 21H10.4907C8.90891 21 8.11803 21 7.49834 20.6997C6.95276 20.4353 6.50009 20.0118 6.19998 19.485C5.85911 18.8867 5.8065 18.0975 5.70129 16.5193L5 6M10 10.5V15.5M14 10.5V15.5" stroke="#D83535" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="file-upload-wrapper">
                                                    <input
                                                        type="file"
                                                        id="file-upload"
                                                        onChange={handleFileChange}
                                                        className="file-upload-input"
                                                        disabled={isSubmitting}
                                                    />
                                                    <label htmlFor="file-upload" className="file-upload-label">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 29 29" fill="none">
                                                            <path d="M14.5 3.90002V17.9M14.5 3.90002L20.3333 9.73336M14.5 3.90002L8.66667 9.73336M25 17.9V22.5667C25 23.1855 24.7542 23.779 24.3166 24.2166C23.879 24.6542 23.2855 24.9 22.6667 24.9H6.33333C5.71449 24.9 5.121 24.6542 4.68342 24.2166C4.24583 23.779 4 23.1855 4 22.5667V17.9" stroke="#212529" strokeOpacity="0.75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <span>File upload (optional)</span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>

                                        {/* Project Description */}
                                        <div className="col-12">
                                            <textarea
                                                name="message"
                                                rows={6}
                                                placeholder="Describe about your project"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                className={`form-control modern-input ${errors.message ? 'is-invalid' : ''}`}
                                                disabled={isSubmitting}
                                                maxLength={1000}
                                            />
                                            {errors.message && (
                                                <div className="custom-select-error">
                                                    {errors.message}
                                                </div>
                                            )}
                                        </div>
                                        {/* Consent Checkbox  d-none */}
                                        <div className="col-12 d-none">
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    name="agreeToContact"
                                                    id="agreeToContact"
                                                    checked={formData.agreeToContact}
                                                    onChange={handleInputChange}
                                                    className={`form-check-input ${errors.agreeToContact ? 'is-invalid' : ''}`}
                                                    disabled={isSubmitting}
                                                />
                                                <label htmlFor="agreeToContact" className="">
                                                    I agree to be contacted and I have read the privacy notice
                                                </label>
                                                {errors.agreeToContact && (
                                                    <div className="custom-select-error">
                                                        {errors.agreeToContact}
                                                    </div>
                                                )}
                                            </div>
                                            <p>By submitting this form I consent to processing my personal data as described in the <span className="fw-bold underline">Privacy Policy</span></p>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="col-12 mt-4">
                                            <button
                                                type="submit"
                                                className="desilo-link-btn btn-pill"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm text-white" role="status" aria-hidden="true"></span>
                                                        Sending...
                                                    </>
                                                ) : (
                                                    <>
                                                        Submit
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div >
            </section >

            {/* Modal Component */}
            < FormModal
                isOpen={modal.isOpen}
                onClose={modal.closeModal}
                onSubmit={handleFormSubmit}
                title="Quick Contact Form"
                submitButtonText="Download"
            />
        </>
    );
};

export default ContactForm;
